// Pure pricing helpers for the subscription price calculator (SubscribersPage).
// No React/axios imports so they can be unit-tested directly.
//
// SOURCE OF TRUTH: SIZE_PRESETS, sizeProfileFor, packMultiplier and packPrice
// are a copy of the storefront's `newnewtdh/src/assets/utils/productUtils.js`,
// so a box is priced pack by pack at shop prices. garageFarmServer's
// `helpers/pricing.js` is another copy: checkout charges with it and the
// TearDrop Assistant's subscription price look-up uses it. If the pricing
// changes in the storefront, change it here too (there is no shared module
// across the repos). `product.price` is the price per 100g, same as the
// storefront.

// Pack sizes the shop sells, by name/category. The smallest value in each
// preset stays >= 20g (Trading Standards minimum order).
export const SIZE_PRESETS = {
  smallHerbs:  [20, 25, 50],
  herbs:       [50, 100],
  leafy:       [50, 100, 200],
  microgreens: [20, 30, 50, 100],
  default:     [30, 50, 100],
};

export function sizeProfileFor(product) {
  const name     = String(product?.name     || '').toLowerCase();
  const category = String(product?.category || '').toLowerCase();
  if (name.includes('chive') || name.includes('dill') || name.includes('coriander'))
    return SIZE_PRESETS.smallHerbs;
  if (category.includes('herbs')) return SIZE_PRESETS.herbs;
  if (category.includes('lettuce') || category.includes('komatsuna') || category.includes('salads'))
    return SIZE_PRESETS.leafy;
  if (category.includes('micro') || category.includes('cress')) return SIZE_PRESETS.microgreens;
  return SIZE_PRESETS.default;
}

// Smaller packs cost more per gram, and every step up is cheaper per gram
// than the one below it.
export function packMultiplier(weight) {
  if (weight <= 20)  return 1.25;
  if (weight <= 30)  return 1.15;
  if (weight <= 50)  return 1.1;
  if (weight <= 100) return 1.0;
  return 0.92;
}

const roundUp10p   = x => Math.ceil(x * 10 - 1e-6) / 10;
const roundDown10p = x => Math.floor(x * 10 + 1e-6) / 10;
const rawPackPrice = (product, grams) =>
  grams * (Number(product?.price) / 100) * packMultiplier(grams);

// Price of one pack in pounds, as the shop shows and charges it. Packs round
// UP to 10p, except where that would make a bigger pack dearer per gram than
// the size below it (chives at £2.79/100g: 20g 70p, 25g would be 90p); that
// pack rounds DOWN instead. A size the shop doesn't sell (say 40g of cress)
// is priced pro rata and rounds up.
export function packPrice(product, grams) {
  const sizes = sizeProfileFor(product);
  if (!sizes.includes(grams)) return roundUp10p(rawPackPrice(product, grams));
  let prevPerGram = Infinity;
  for (const g of sizes) {
    const raw = rawPackPrice(product, g);
    let price = roundUp10p(raw);
    if (price / g > prevPerGram + 1e-9) price = roundDown10p(raw);
    if (g === grams) return price;
    prevPerGram = price / g;
  }
}

// Shop price of one `grams` pack of `product` (packPrice), before quantity.
// Returns a number (£), or null if we can't price it (no product, no price,
// no valid grams).
export function linePriceForGrams(product, grams) {
  const g = Number(grams);
  if (!product || !Number.isFinite(g) || g <= 0) return null;
  if (!Number.isFinite(Number(product.price))) return null;
  return packPrice(product, g);
}

// Pull a gram figure out of the free-text quantity the customer typed
// ("200g", "200 g", "2 x 100g", "150"). Returns grams as a number, or null
// for non-weight quantities ("1 bunch", "a punnet", "").
export function parseGrams(text) {
  const t = String(text || '').toLowerCase().trim();
  if (!t) return null;
  // "2 x 100g" / "2x100 g" -> multiply
  const multi = t.match(/(\d+(?:\.\d+)?)\s*[x*]\s*(\d+(?:\.\d+)?)\s*g\b/);
  if (multi) return Number(multi[1]) * Number(multi[2]);
  const withUnit = t.match(/(\d+(?:\.\d+)?)\s*g(?:ram)?s?\b/);
  if (withUnit) return Number(withUnit[1]);
  // bare number, only if nothing else looks unit-y
  const bare = t.match(/^(\d+(?:\.\d+)?)$/);
  if (bare) return Number(bare[1]);
  return null;
}

const normalise = (s) =>
  String(s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

// Best-guess catalogue match for a free-text item name. Returns the product
// object or null. Deliberately conservative - the admin can always re-pick.
export function matchProduct(name, products) {
  const q = normalise(name);
  if (!q || !Array.isArray(products) || products.length === 0) return null;

  const qTokens = q.split(' ').filter(Boolean);
  let best = null;
  let bestScore = 0;

  for (const p of products) {
    const pn = normalise(p.name);
    if (!pn) continue;
    let score = 0;
    if (pn === q) score = 100;
    else if (pn.includes(q) || q.includes(pn)) score = 50;
    else {
      const pTokens = new Set(pn.split(' ').filter(Boolean));
      const overlap = qTokens.filter((t) => pTokens.has(t)).length;
      if (overlap > 0) score = overlap * 10 - Math.abs(pTokens.size - qTokens.length);
    }
    if (score > bestScore) { best = p; bestScore = score; }
  }
  return bestScore > 0 ? best : null;
}

// Round a £ figure to the nearest step (default 5p) for a tidy final price.
export function roundPrice(value, step = 0.05) {
  const v = Number(value);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v / step) * step;
}

const pennies = (n) => Math.round(n * 100) / 100;

// Total a set of calculator rows + options into a price-per-delivery breakdown.
// row: { price: number|null, include: bool }
// Items, discount and total are in whole pence, rounded the same way as the
// TearDrop Assistant's subscription price look-up (garageFarmServer
// helpers/assistantLookups.js), so the figures shown add up and the two agree.
export function summarise(rows, { discountPct = 0, deliveryFee = 0 } = {}) {
  const hasPrice = (r) => r.price != null && r.price !== '' && Number.isFinite(Number(r.price));
  const priced = rows.filter((r) => r.include && hasPrice(r));
  const itemsTotal = pennies(priced.reduce((sum, r) => sum + Number(r.price), 0));
  const unpriced = rows.filter((r) => r.include && !hasPrice(r)).length;
  const pct = Math.min(100, Math.max(0, Number(discountPct) || 0));
  const discount = pennies(itemsTotal * pct / 100);
  const fee = Number(deliveryFee) || 0;
  const total = pennies(itemsTotal - discount + fee);
  return { itemsTotal, discount, deliveryFee: fee, total, unpriced };
}
